<template>
  <div
    class="imageteaser-holder is-clickable"
    :class="{ 'image-is-private': image.private, hoverable }"
  >
    <div
      v-if="showNsfwInfo"
      class="teaser-nsfw-information"
      @click.stop="nsfwToggled = true"
    >
      😳 NSFW (click to show)
    </div>
    <v-card
      class="imageteaser"
      elevation="10"
      :style="styles"
      @click="onClick"
    >
      <div
        v-if="image.aiGenerated"
        class="imageteaser-ai-badge"
        title="AI generated"
      >
        <v-icon
          icon="mdi-robot"
          size="small"
        /> AI
      </div>
      <div class="imageteaser-inner">
        <div
          v-if="imageStateInfo"
          :class="`imageteaser-info ${imageStateInfo.class}`"
          :title="imageStateInfo.title"
        >
          <v-icon :icon="imageStateInfo.icon" /> {{ imageStateInfo.text }}
        </div>
        <h4 class="imageteaser-title">
          {{ image.title || '&lt;No Title&gt;' }}
        </h4>

        <div
          v-tooltip="'Report this image'"
          class="report-button imageteaser-report"
          @click.stop="openReportImageDialog({ image })"
        />

        <div
          v-if="image.copyrightName || image.copyrightURL"
          class="imageteaser-info"
        >
          <v-icon icon="mdi-copyright" />
          <a
            v-if="image.copyrightURL"
            :href="image.copyrightURL"
            target="_blank"
            class="ml-1"
          >{{ image.copyrightName || 'Source' }} <v-icon icon="mdi-open-in-new" /></a>
          <span
            v-else
            class="ml-1"
          >{{ image.copyrightName }}</span>
        </div>
        <div class="imageteaser-info">
          <v-icon icon="mdi-motion-play" /> {{ image.gameCount }}x plays
        </div>
        <div class="imageteaser-info">
          <v-icon icon="mdi-calendar-month" /> {{ date }}
        </div>
        <div class="imageteaser-info">
          <v-icon icon="mdi-ruler-square" /> {{ image.width }}x{{ image.height }}
        </div>
        <div
          v-if="image.tags.length"
          class="imageteaser-info"
        >
          <v-icon icon="mdi-tag" /> {{ image.tags.map((t: Tag) => t.title).join(', ') }}
        </div>
        <div class="imageteaser-click-info">
          <h5>Click to setup a game</h5>
        </div>
        <div class="imageteaser-actions">
          <v-btn
            v-if="canEdit"
            variant="text"
            icon="mdi-pencil"
            size="x-small"
            class="imageteaser-edit"
            @click.stop="onEditClick"
          />
        </div>
      </div>
    </v-card>
  </div>
</template>
<script setup lang="ts">
import { computed, ref } from 'vue'
import { resizeUrl } from '@common/ImageService'
import type { ImageInfo, Tag } from '@common/Types'
import { IMAGE_STATES_PENDING, IMAGE_STATES_REJECTED } from '@common/Types'
import { useDialog } from '../../useDialog'
import { me, useNsfw } from '../../user'

const { openReportImageDialog } = useDialog()

const props = withDefaults(defineProps<{
  image: ImageInfo
  edit?: boolean
}>(), {
  edit: true,
})

const { nsfwUnblurred } = useNsfw()
const nsfwToggled = ref<boolean>(false)
const hoverable = computed(() => (!props.image.nsfw || nsfwUnblurred.value || nsfwToggled.value))
const showNsfwInfo = computed(() => props.image.nsfw && !nsfwUnblurred.value && !nsfwToggled.value)

const emit = defineEmits<{
  (event: 'click'): void
  (event: 'editClick'): void
}>()

const url = computed(() => resizeUrl(props.image.url, 375, 0, 'cover'))

const aspectRatio = computed(() => props.image.width / props.image.height)

const MIN_HEIGHT = 300

const styles = computed(() => {
  const height = Math.max(MIN_HEIGHT, props.image.height)
  const width = height * aspectRatio.value
  const base: Record<string, string> = {
    paddingTop: (height / width * 100) + '%',
  }
  base.backgroundImage = `url('${url.value}')`
  base.backgroundSize = 'cover'
  base.backgroundPosition = '50% 50%'
  return base
})

const date = computed((): string => {
  // TODO: use date format that is same everywhere
  return new Date(parseInt(`${props.image.created}`, 10)).toLocaleDateString()
})

const canEdit = computed((): boolean => {
  if (!props.edit) {
    return false
  }
  if (!me.value || !me.value.id) {
    return false
  }
  return me.value.id === props.image.uploaderUserId
})

const imageStateInfo = computed((): { class: string, text: string, title?: string, icon: string } | null => {
  if (props.image.private) {
    return {
      class: 'image-is-private-info',
      text: 'Private Image',
      icon: 'mdi-incognito',
    }
  }

  if (IMAGE_STATES_PENDING.includes(props.image.state)) {
    return {
      class: 'image-is-pending-info',
      text: 'Image pending review',
      icon: 'mdi-clock-outline',
    }
  }

  if (IMAGE_STATES_REJECTED.includes(props.image.state)) {
    return {
      class: 'image-is-rejected-info',
      text: 'Image not prominently shown',
      title: props.image.rejectReason || '',
      icon: 'mdi-close-circle-outline',
    }
  }

  return null
})

const onClick = (ev: Event) => {
  const target = ev.target as HTMLElement
  if (
    target.tagName === 'A'
    || (target.parentElement && target.parentElement.tagName === 'A')
  ) {
    return
  }
  emit('click')
}

const onEditClick = () => {
  emit('editClick')
}
</script>
